import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import DoctorDashboard from "@/pages/DoctorDashboard";
// PatientCaregiverDashboard replaced by role-specific PatientPortal / CaregiverPortal
import NewPatient from "@/pages/NewPatient";
import PatientDetail from "@/pages/PatientDetail";
import PatientEdit from "@/pages/PatientEdit";
import NewConsultation from "@/pages/NewConsultation";
import ConsultationDetail from "@/pages/ConsultationDetail";
import PatientIntake from "@/pages/PatientIntake";
import CalendarPage from "@/pages/CalendarPage";
import RemindersPage from "@/pages/RemindersPage";
import BillingPage from "@/pages/BillingPage";
import MonitoringPage from "@/pages/MonitoringPage";
import PatientRiskDetail from "@/pages/PatientRiskDetail";
import CaregiverMonitoring from "@/pages/CaregiverMonitoring";
import PatientPortal from "@/pages/PatientPortal";
import CaregiverPortal from "@/pages/CaregiverPortal";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import ContentAdminPage from "@/pages/ContentAdminPage";
import PlanEditorPage from "@/pages/PlanEditorPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import PatientsListPage from "@/pages/PatientsListPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

const PROFESSIONAL_ROLES = new Set([
    "doctor", "nurse", "physiotherapist", "occupational_therapist", "nutritionist",
    "psychologist", "social_worker", "pharmacist", "transpersonal_therapist",
]);
const ALL_PROS = ["admin", "doctor", "nurse", "physiotherapist", "occupational_therapist",
    "nutritionist", "psychologist", "social_worker", "pharmacist", "transpersonal_therapist"];
const DOCTOR_ADMIN = ["doctor", "admin"];

function DashboardRouter() {
    const { user } = useAuth();
    if (!user) return null;
    if (user.role === "admin" || PROFESSIONAL_ROLES.has(user.role)) return <DoctorDashboard />;
    if (user.role === "caregiver") return <CaregiverPortal />;
    return <PatientPortal />;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="top-right" richColors />
                <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/intake/:token" element={<PatientIntake />} />

                    {/* Público — Minuto da Saúde */}
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/depoimentos" element={<TestimonialsPage />} />

                    <Route path="/admin/content" element={<ProtectedRoute roles={["doctor", "admin"]}><ContentAdminPage /></ProtectedRoute>} />
                    <Route path="/admin/users" element={<ProtectedRoute roles={["doctor", "admin"]}><AdminUsersPage /></ProtectedRoute>} />

                    <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
                    <Route path="/patients/new" element={<ProtectedRoute roles={DOCTOR_ADMIN}><NewPatient /></ProtectedRoute>} />
                    <Route path="/patients" element={<ProtectedRoute roles={ALL_PROS}><PatientsListPage /></ProtectedRoute>} />
                    <Route path="/patients/:id/edit" element={<ProtectedRoute roles={DOCTOR_ADMIN}><PatientEdit /></ProtectedRoute>} />
                    <Route path="/patients/:id" element={<ProtectedRoute roles={ALL_PROS}><PatientDetail /></ProtectedRoute>} />
                    <Route path="/consultations/new" element={<ProtectedRoute roles={DOCTOR_ADMIN}><NewConsultation /></ProtectedRoute>} />
                    <Route path="/consultations/:id" element={<ProtectedRoute roles={ALL_PROS}><ConsultationDetail /></ProtectedRoute>} />
                    <Route path="/consultations/:id/plan-editor" element={<ProtectedRoute roles={DOCTOR_ADMIN}><PlanEditorPage /></ProtectedRoute>} />
                    <Route path="/calendar" element={<ProtectedRoute roles={ALL_PROS}><CalendarPage /></ProtectedRoute>} />
                    <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
                    <Route path="/billing" element={<ProtectedRoute roles={DOCTOR_ADMIN}><BillingPage /></ProtectedRoute>} />
                    <Route path="/monitoring" element={<ProtectedRoute roles={ALL_PROS}><MonitoringPage /></ProtectedRoute>} />
                    <Route path="/monitoring/patient/:patientId" element={<ProtectedRoute roles={ALL_PROS}><PatientRiskDetail /></ProtectedRoute>} />
                    <Route path="/caregiver-monitoring" element={<ProtectedRoute roles={ALL_PROS}><CaregiverMonitoring /></ProtectedRoute>} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
